import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, filter, firstValueFrom, timeout } from 'rxjs';
import { exotelSipConfig } from './exotel-sip.config';
import { ExotelSipConfig, ExotelSipUser } from './exotel-sip.model';
import { Constants } from 'src/app/constants/constants';
import { CubeDetails, LoggedInUserDetails } from 'src/app/models/LoggedInUserDetails';
import { HomeService } from 'src/app/modules/home/home.service';
import { IvrCallsService } from 'src/app/modules/shared/components/ivr-calls/ivr-calls.service';
import { ExotelCallHandle, ExotelWebClientInstance } from './exotel-sdk';

export type ExotelRegistration = 'offline' | 'registering' | 'registered' | 'error';
export type ExotelCallPhase = 'idle' | 'incoming' | 'ringing' | 'connected';

export interface ExotelPhoneState {
  registration: ExotelRegistration;
  phase: ExotelCallPhase;
  remoteNumber: string;
  callType: string;
  muted: boolean;
  held: boolean;
  connectedAt: number | null;
  statusLabel: string;
  message: string;
  sipUser: string;
  /** True while the task popup owns the live-call screen. */
  taskHosted: boolean;
}

export interface ExotelOutboundContext {
  participationId?: string | number;
  leadId?: string | number;
  programTaskId?: string | number;
  callType?: string;
  /** ExoPhone / channel that Exotel should present as caller ID. */
  ivrNumber?: string;
  ivrCategoryId?: string | number;
  ivrSubCategoryId?: string | number;
  notes?: string;
}

export interface ExotelPlaceHooks {
  onPlaced?: (response: any) => void;
  onFailed?: (message: string) => void;
}

const SIP_DEVICE_KEY = 'sip_device_enabled';
const SIP_DEVICE_OWNER_KEY = 'sip_device_owner';

const initialState: ExotelPhoneState = {
  registration: 'offline',
  phase: 'idle',
  remoteNumber: '',
  callType: 'Dial Call',
  muted: false,
  held: false,
  connectedAt: null,
  statusLabel: '',
  message: '',
  sipUser: '',
  taskHosted: false,
};

@Injectable({ providedIn: 'root' })
export class ExotelWebrtcService {
  readonly state$ = new BehaviorSubject<ExotelPhoneState>(initialState);

  private client: ExotelWebClientInstance | null = null;
  /** Call object from the SDK event. getCall() is empty while the leg is still ringing. */
  private activeCall: ExotelCallHandle | null = null;
  private sdkPromise: Promise<void> | null = null;
  private initialized = false;
  private initializedFor = '';
  private sipRequested = false;
  private hadRegistration = false;
  private registerTask: Promise<void> | null = null;
  private readonly tabId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  private readonly onSipStorage = (event: StorageEvent): void => {
    if (event.key === SIP_DEVICE_OWNER_KEY) {
      if (event.newValue && event.newValue !== this.tabId) {
        this.zone.run(() => this.releaseLocalRegistration());
      }
      return;
    }
    if (event.key !== SIP_DEVICE_KEY || event.newValue === '1') {
      return;
    }
    this.zone.run(() => {
      void this.setSipEnabled(false);
    });
  };
  private awaitingAnswer = false;
  /** True while Exotel is ringing this browser for a call we just placed. */
  private outboundLeg = false;
  /** Task dialog owns this agent leg, so the floating Accept/Reject phone stays hidden. */
  private taskOwnsAgentLeg = false;
  private agentLegTimer: ReturnType<typeof setTimeout> | null = null;
  /** SIP username and secret returned by member_connect/ensure_sip_device. */
  private coachCube: CubeDetails | null = null;
  /** One password refresh after Exotel rejects the saved SIP secret. */
  private sipSecretRefreshUsed = false;

  constructor(
    private zone: NgZone,
    private ivrCalls: IvrCallsService,
    private home: HomeService,
  ) {
    window.addEventListener('storage', this.onSipStorage);
    window.addEventListener('beforeunload', () => {
      if (this.sipRequested) {
        this.client?.UnRegister();
      }
    });
  }

  get snapshot(): ExotelPhoneState {
    return this.state$.value;
  }

  /** True when the coach turned the SIP device on. Shared across tabs. */
  get sipDeviceOn(): boolean {
    return this.readSipEnabled();
  }

  /**
   * Makes this tab the registered SIP device when the switch is on.
   * Returns false when the switch is off, so the caller can use the phone flow.
   */
  async prepareForExophoneCall(): Promise<boolean> {
    const registration = (): ExotelRegistration => this.state$.value.registration;
    if (!this.sipDeviceOn && registration() === 'offline') {
      return false;
    }
    this.claimSipOwner();
    if (registration() !== 'registered') {
      await this.setSipEnabled(true);
    }
    const afterStart = registration();
    if (!this.sipDeviceOn || afterStart === 'offline' || afterStart === 'error') {
      return false;
    }
    if (afterStart === 'registered') {
      return true;
    }
    try {
      const state = await firstValueFrom(
        this.state$.pipe(
          filter((next) => next.registration === 'registered' || next.registration === 'error' || next.registration === 'offline'),
          timeout(15000),
        ),
      );
      return state.registration === 'registered';
    } catch {
      return registration() === 'registered';
    }
  }

  /**
   * The next incoming ring is the agent leg of a call this task just placed.
   * Answer it in the browser and keep the floating phone hidden.
   */
  claimTaskAgentLeg(number: string): void {
    if (this.snapshot.registration !== 'registered') {
      return;
    }
    this.outboundLeg = true;
    this.taskOwnsAgentLeg = true;
    this.patch({ remoteNumber: number });
    this.clearAgentLegTimer();
    this.agentLegTimer = setTimeout(() => {
      this.zone.run(() => {
        if (this.taskOwnsAgentLeg && this.snapshot.phase === 'idle') {
          this.taskOwnsAgentLeg = false;
          this.outboundLeg = false;
        }
      });
    }, 20000);
  }

  /** Drop the claim when the call was not placed, or the task dialog closes. */
  releaseTaskAgentLeg(): void {
    this.clearAgentLegTimer();
    if (!this.taskOwnsAgentLeg) {
      return;
    }
    this.taskOwnsAgentLeg = false;
    if (this.snapshot.phase === 'idle') {
      this.outboundLeg = false;
      return;
    }
    this.setTaskHost(false);
  }

  /** Keep the floating phone hidden while the task popup is showing this call. */
  setTaskHost(hosted: boolean): void {
    if (this.snapshot.taskHosted === hosted) {
      return;
    }
    this.patch({ taskHosted: hosted });
  }

  /** Unregister this browser and clear the SIP switch. Used on logout. */
  releaseForLogout(): void {
    if (!this.sipDeviceOn && this.snapshot.registration === 'offline') {
      return;
    }
    void this.setSipEnabled(false);
  }

  async setSipEnabled(enabled: boolean): Promise<void> {
    if (enabled && !this.canUseSipDevice()) {
      this.persistSipDevice(false);
      this.sipRequested = false;
      this.patch({
        registration: 'offline',
        sipUser: '',
        message: 'ExoPhone calling is not enabled for this coach.',
      });
      return;
    }
    this.persistSipDevice(enabled);
    if (!enabled) {
      this.sipRequested = false;
      this.hadRegistration = false;
      this.sipSecretRefreshUsed = false;
      this.registerTask = null;
      this.clearSipOwner();
      this.client?.cancelPendingRegister?.();
      this.client?.UnRegister();
      this.clearCall();
      this.patch({ registration: 'offline', message: '', sipUser: '' });
      return;
    }
    if (this.snapshot.registration === 'registered' && this.sipRequested) {
      return;
    }
    if (this.registerTask) {
      return this.registerTask;
    }

    this.sipRequested = true;
    this.registerTask = this.startRegistration().finally(() => {
      this.registerTask = null;
    });
    return this.registerTask;
  }

  private async startRegistration(refresh = false): Promise<void> {
    this.claimSipOwner();
    this.patch({ registration: 'registering', message: '', sipUser: '' });
    const ready = await this.loadSipCredentials(refresh);
    if (!this.sipRequested || !ready) {
      return;
    }

    const resolved = this.resolvedAccount();
    const account = resolved ? this.registrationAccount(resolved) : null;
    if (!resolved || !account) {
      this.patch({
        registration: 'error',
        sipUser: '',
        message: this.missingSipMessage(),
      });
      return;
    }
    const sipUserName = resolved.user.userName.replace(/^sip:/i, '');

    if (this.initialized && this.initializedFor !== sipUserName) {
      this.client?.UnRegister();
      this.client = null;
      this.initialized = false;
      this.sdkPromise = null;
    }

    this.patch({ registration: 'registering', message: '', sipUser: sipUserName });
    try {
      await this.ensureClient();
      if (!this.client) {
        throw new Error('Exotel WebRTC SDK did not load.');
      }
      if (!this.initialized) {
        const ready = await this.client.initWebrtc(
          account,
          (state) => this.onRegister(state),
          (call, eventType, remoteName) => this.onCall(call, eventType, remoteName),
          (state) => this.onSession(state)
        );
        if (!ready) {
          throw new Error('Exotel WebRTC could not be initialized.');
        }
        this.initialized = true;
        this.initializedFor = sipUserName;
      }
      if (!this.sipRequested) {
        return;
      }
      const started = this.client.DoRegister();
      if (!started) {
        throw new Error('SIP registration did not start.');
      }
    } catch (error) {
      this.patch({
        registration: 'error',
        message: error instanceof Error ? error.message : 'Could not register the SIP device.',
      });
    }
  }

  accept(): void {
    this.awaitingAnswer = false;
    this.outboundLeg = false;
    this.liveCall()?.Answer();
    this.patch({ phase: 'ringing', statusLabel: 'Connecting' });
  }

  reject(): void {
    this.awaitingAnswer = false;
    this.outboundLeg = false;
    this.liveCall()?.Hangup();
    this.clearCall();
  }

  hangup(): void {
    this.awaitingAnswer = false;
    this.outboundLeg = false;
    this.liveCall()?.Hangup();
    this.clearCall();
  }

  toggleMute(): void {
    const call = this.liveCall();
    if (!call || !this.onLiveCall()) {
      return;
    }
    try {
      if (call.MuteToggle) {
        call.MuteToggle();
      } else if (this.snapshot.muted) {
        call.UnMute();
      } else {
        call.Mute();
      }
    } catch {
      this.patch({ message: 'Mute is available once the call audio is connected.' });
    }
  }

  toggleHold(): void {
    const call = this.liveCall();
    if (!call || !this.onLiveCall()) {
      return;
    }
    try {
      if (call.HoldToggle) {
        call.HoldToggle();
      } else if (this.snapshot.held) {
        call.UnHold();
      } else {
        call.Hold();
      }
    } catch {
      this.patch({ message: 'Hold is available once the call audio is connected.' });
    }
  }

  private onLiveCall(): boolean {
    return this.snapshot.phase === 'ringing' || this.snapshot.phase === 'connected';
  }

  sendDigit(digit: string): void {
    if (this.snapshot.phase !== 'connected') {
      return;
    }
    this.liveCall()?.sendDTMF(digit);
  }
  
  /**
   * Places the call through the existing member_connect/initiate_call API.
   * From is the registered SIP user so Exotel rings this browser, records the call, and posts the recording to update_patient_call_log.
   */
  placeCall(number: string, context: ExotelOutboundContext = {}, hooks: ExotelPlaceHooks = {}): void {
    const failEarly = (message: string) => {
      this.patch({ message });
      hooks.onFailed?.(message);
    };
    const resolved = this.resolvedAccount();
    const sipUser = (resolved?.user.userName || '').replace(/^sip:/i, '').trim();
    if (!resolved || !sipUser) {
      failEarly(this.missingSipMessage());
      return;
    }
    if (!context.participationId && !context.leadId) {
      failEarly('Open a member or lead before calling.');
      return;
    }

    const payload: Record<string, any> = {
      mobile_no_from: `sip:${sipUser}`,
      mobile_no_to: number,
      ivr_number: (context.ivrNumber || resolved.config.callerId || '').trim() || '09240920016',
      is_manual_call: false,
    };
    if (context.callType) {
      payload['call_type'] = context.callType;
    }
    if (context.leadId) {
      payload['lead_id'] = context.leadId;
    } else if (context.participationId) {
      payload['participation_id'] = context.participationId;
    }
    if (context.programTaskId) {
      payload['program_task_id'] = context.programTaskId;
    }
    if (context.ivrCategoryId) {
      payload['ivr_category_id'] = context.ivrCategoryId;
    }
    if (context.ivrSubCategoryId) {
      payload['ivr_sub_category_id'] = context.ivrSubCategoryId;
    }
    if (context.notes) {
      payload['notes'] = context.notes;
    }

    this.outboundLeg = true;
    this.patch({
      phase: 'ringing',
      remoteNumber: number,
      callType: context.callType || 'Dial Call',
      statusLabel: 'Calling',
      message: '',
      muted: false,
      held: false,
      connectedAt: null,
    });

    this.ivrCalls.initiateCall(payload).subscribe({
      next: (response) => {
        if (response?.data) {
          hooks.onPlaced?.(response);
          return;
        }
        this.failOutbound(response?.message || 'Exotel did not place the call.', hooks);
      },
      error: (error) => {
        const detail = error?.error?.message || error?.message || '';
        this.failOutbound(detail || 'Exotel did not place the call.', hooks);
      },
    });
  }

  private failOutbound(message: string, hooks: ExotelPlaceHooks = {}): void {
    this.outboundLeg = false;
    this.clearCall();
    this.patch({ message });
    hooks.onFailed?.(message);
  }

  private missingSipMessage(): string {
    if (!exotelSipConfig.sipdomain || !exotelSipConfig.domain) {
      return 'Exotel SIP settings are missing.';
    }
    const details = this.loggedInCoach();
    const name = (details?.name || details?.email || '').trim();
    return name
      ? `${name} needs a SIP Username and SIP secret.`
      : 'This coach needs a SIP Username and SIP secret.';
  }

  private loggedInCoach(): LoggedInUserDetails {
    return (this.home.getUserDetails() || {}) as LoggedInUserDetails;
  }

  /** ExoPhone is limited to Abhi coaches who have ozonetel calling access. */
  private canUseSipDevice(): boolean {
    const client = this.home.getCurrentClient();
    return client?.path_key === Constants.ABHI_PATH_KEY && !!this.loggedInCoach().ozonetel_calling_access;
  }

  /**
   * Backend maps the coach in Exotel when needed and returns AgentId (SipId)
   * plus web_url_password (SipSecret).
   */
  private async loadSipCredentials(refresh: boolean): Promise<boolean> {
    try {
      const response = await firstValueFrom(this.ivrCalls.ensureSipDevice(refresh));
      if (!this.sipRequested) {
        return false;
      }
      const creds = this.readEnsuredSip(response);
      if (!creds) {
        this.patch({
          registration: 'error',
          sipUser: '',
          message: response?.message || 'SIP credentials were not returned.',
        });
        return false;
      }
      this.applySipCredentials(creds.agentId, creds.secret);
      return true;
    } catch (error) {
      if (!this.sipRequested) {
        return false;
      }
      this.patch({
        registration: 'error',
        sipUser: '',
        message: this.apiErrorMessage(error),
      });
      return false;
    }
  }

  private readEnsuredSip(response: any): { agentId: string; secret: string } | null {
    const body = response?.contents ?? response?.data ?? {};
    const cube = body?.cube_details && typeof body.cube_details === 'object' ? body.cube_details : body;
    const agentId = String(cube?.AgentId || '').trim().replace(/^sip:/i, '');
    const secret = String(cube?.web_url_password || '').trim();
    if (!agentId || !secret) {
      return null;
    }
    return { agentId, secret };
  }

  private applySipCredentials(agentId: string, secret: string): void {
    const coach = this.loggedInCoach();
    const cube: CubeDetails = {
      ...(this.coachCube || coach.cube_details || {}),
      AgentId: agentId,
      web_url_password: secret,
    };
    this.coachCube = cube;
    this.home.setUserDetails({ ...coach, cube_details: cube });
  }

  private apiErrorMessage(error: unknown): string {
    const body = (error as { error?: { message?: string } | string })?.error;
    const message = typeof body === 'string' ? body : body?.message;
    return (message || '').trim() || 'Could not prepare the SIP device.';
  }

  /** Cube Agent ID is the SIP user. Cube Web URL Password is the SIP secret. */
  private selectedUser(): ExotelSipUser | null {
    const details = this.loggedInCoach();
    const cube = this.coachCube || details.cube_details;
    const agentId = (cube?.AgentId || '').trim().replace(/^sip:/i, '');
    const secret = (cube?.web_url_password || '').trim();
    if (!agentId || !secret) {
      return null;
    }
    return {
      userName: agentId,
      authUser: agentId,
      displayname: (details.name || agentId).trim(),
      secret,
      appUserId: agentId,
    };
  }

  private resolvedAccount(): { config: ExotelSipConfig; user: ExotelSipUser } | null {
    const user = this.selectedUser();
    if (!user || !exotelSipConfig.sipdomain || !exotelSipConfig.domain) {
      return null;
    }
    return { config: exotelSipConfig, user };
  }

  private registrationAccount(resolved: { config: ExotelSipConfig; user: ExotelSipUser }): Record<string, string> {
    const { config, user } = resolved;
    const port = config.port || '443';
    const domain = config.domain.includes(':') ? config.domain : `${config.domain}:${port}`;
    const userName = user.userName.replace(/^sip:/i, '');
    const authUser = (user.authUser || userName).replace(/^sip:/i, '');
    return {
      userName,
      authUser,
      displayname: user.displayname || user.userName,
      displayName: user.displayname || user.userName,
      secret: user.secret,
      sipdomain: config.sipdomain,
      domain,
      port,
      security: config.security || 'wss',
      endpoint: config.endpoint || 'wss',
    };
  }

  private ensureClient(): Promise<void> {
    if (this.client) {
      return Promise.resolve();
    }
    if (!this.sdkPromise) {
      this.sdkPromise = this.loadSdk().catch((error) => {
        this.sdkPromise = null;
        throw error;
      });
    }
    return this.sdkPromise;
  }

  private loadSdk(): Promise<void> {
    const existing = window.exotelSDK?.ExotelWebClient;
    if (existing) {
      this.client = new existing();
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = new URL('exotel-sdk/exotelsdk.js', document.baseURI).toString();
      script.async = false;
      script.onload = () => {
        const Ctor = window.exotelSDK?.ExotelWebClient;
        if (!Ctor) {
          reject(new Error('Exotel WebRTC SDK did not load.'));
          return;
        }
        this.client = new Ctor();
        resolve();
      };
      script.onerror = () => reject(new Error('Could not load the Exotel WebRTC SDK.'));
      document.body.appendChild(script);
    });
  }

  private onRegister(state: string): void {
    this.zone.run(() => {
      if (state === 'registered') {
        this.hadRegistration = true;
        this.sipSecretRefreshUsed = false;
        this.patch({ registration: 'registered', message: '' });
        return;
      }
      if (state === 'sent_request') {
        this.patch({ registration: 'registering' });
        return;
      }
      if (state === 'unregistered' || state === 'terminated') {
        const movedToAnotherTab = this.hadRegistration && this.sipRequested;
        this.hadRegistration = false;
        if (this.sipRequested && !movedToAnotherTab && !this.sipSecretRefreshUsed) {
          this.sipSecretRefreshUsed = true;
          this.initialized = false;
          this.initializedFor = '';
          this.client?.cancelPendingRegister?.();
          this.client = null;
          this.sdkPromise = null;
          void this.startRegistration(true);
          return;
        }
        this.patch({
          registration: this.sipRequested ? 'error' : 'offline',
          message: this.sipRequested
            ? (movedToAnotherTab
              ? 'SIP device is connected in another tab.'
              : 'Exotel rejected the SIP password for this username.')
            : '',
        });
      }
    });
  }

  private liveCall(): ExotelCallHandle | null {
    const wrapped = this.client?.getCall?.();
    if (wrapped?.Answer) {
      return wrapped;
    }
    return this.activeCall?.Answer ? this.activeCall : wrapped || this.activeCall;
  }

  private onCall(call: ExotelCallHandle | null, eventType: string, remoteName?: string): void {
    this.zone.run(() => {
      if (call) {
        this.activeCall = call;
      }
      const number = remoteName || call?.remoteDisplayName || call?.remoteId || this.snapshot.remoteNumber;
      if (eventType === 'incoming') {
        if (this.outboundLeg) {
          this.outboundLeg = false;
          this.awaitingAnswer = false;
          this.clearAgentLegTimer();
          this.liveCall()?.Answer();
          this.patch({
            phase: 'ringing',
            remoteNumber: this.snapshot.remoteNumber || number,
            statusLabel: 'Connecting',
            message: '',
            ...(this.taskOwnsAgentLeg ? { taskHosted: true } : {}),
          });
          return;
        }
        this.awaitingAnswer = true;
        this.patch({
          phase: 'incoming',
          remoteNumber: number,
          callType: 'Dial Call',
          statusLabel: 'Incoming',
          muted: false,
          held: false,
          connectedAt: null,
          message: '',
        });
        return;
      }
      if (eventType === 'ringing') {
        if (this.awaitingAnswer || this.snapshot.phase === 'connected' || this.snapshot.connectedAt) {
          return;
        }
        this.patch({
          phase: 'ringing',
          remoteNumber: this.taskOwnsAgentLeg ? (this.snapshot.remoteNumber || number) : number,
          statusLabel: 'Calling',
          message: '',
        });
        return;
      }
      if (eventType === 'connected') {
        this.awaitingAnswer = false;
        this.patch({
          phase: 'connected',
          remoteNumber: this.taskOwnsAgentLeg ? (this.snapshot.remoteNumber || number) : number,
          statusLabel: this.snapshot.held ? 'On hold' : 'Connected',
          connectedAt: this.snapshot.connectedAt ?? Date.now(),
          message: '',
        });
        return;
      }
      if (eventType === 'callEnded' || eventType === 'terminated') {
        this.awaitingAnswer = false;
        this.outboundLeg = false;
        const releaseHost = this.taskOwnsAgentLeg;
        this.taskOwnsAgentLeg = false;
        this.clearAgentLegTimer();
        this.clearCall(releaseHost);
      }
    });
  }

  private onSession(state: string): void {
    this.zone.run(() => {
      if (state === 'media_permission_denied') {
        this.patch({ message: 'Allow microphone access to use the softphone.' });
        return;
      }
      if (state === 'accepted' && this.onLiveCall()) {
        this.patch({
          phase: 'connected',
          statusLabel: this.snapshot.held ? 'On hold' : 'Connected',
          connectedAt: this.snapshot.connectedAt ?? Date.now(),
        });
        return;
      }
      if (state === 'muted') {
        this.patch({ muted: true });
        return;
      }
      if (state === 'unmuted') {
        this.patch({ muted: false });
        return;
      }
      if (state === 'holdunhold_success') {
        const held = !this.snapshot.held;
        this.patch({
          held,
          statusLabel: held ? 'On hold' : 'Connected',
        });
        return;
      }
      if (state === 'holdunhold_failed') {
        this.patch({ message: 'Exotel did not change the hold state.' });
      }
    });
  }

  private clearCall(releaseTaskHost = false): void {
    this.activeCall = null;
    this.patch({
      phase: 'idle',
      remoteNumber: '',
      muted: false,
      held: false,
      connectedAt: null,
      statusLabel: '',
      callType: 'Dial Call',
      ...(releaseTaskHost ? { taskHosted: false } : {}),
    });
  }

  private clearAgentLegTimer(): void {
    if (this.agentLegTimer) {
      clearTimeout(this.agentLegTimer);
      this.agentLegTimer = null;
    }
  }

  private patch(partial: Partial<ExotelPhoneState>): void {
    this.state$.next({ ...this.snapshot, ...partial });
  }

  private releaseLocalRegistration(): void {
    if (!this.sipRequested && this.snapshot.registration === 'offline' && this.snapshot.phase === 'idle') {
      return;
    }
    this.sipRequested = false;
    this.hadRegistration = false;
    this.registerTask = null;
    this.outboundLeg = false;
    this.awaitingAnswer = false;
    this.taskOwnsAgentLeg = false;
    this.clearAgentLegTimer();
    this.client?.cancelPendingRegister?.();
    this.client?.UnRegister();
    this.clearCall();
    this.patch({ registration: 'offline', message: '', sipUser: '', taskHosted: false });
  }

  private claimSipOwner(): void {
    try {
      localStorage.setItem(SIP_DEVICE_OWNER_KEY, this.tabId);
    } catch {
      return;
    }
  }

  private clearSipOwner(): void {
    try {
      if (localStorage.getItem(SIP_DEVICE_OWNER_KEY) === this.tabId) {
        localStorage.removeItem(SIP_DEVICE_OWNER_KEY);
      }
    } catch {
      return;
    }
  }

  private readSipEnabled(): boolean {
    try {
      return localStorage.getItem(SIP_DEVICE_KEY) === '1';
    } catch {
      return false;
    }
  }

  private persistSipDevice(enabled: boolean): void {
    try {
      if (this.readSipEnabled() === enabled) {
        return;
      }
      if (enabled) {
        localStorage.setItem(SIP_DEVICE_KEY, '1');
      } else {
        localStorage.removeItem(SIP_DEVICE_KEY);
      }
    } catch {
      return;
    }
  }
}
